import { type AirServerError, isAirAdmin } from '@airreading/common';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { getCoach, getUser } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';

import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';

import { SsoEditableInfoCard } from '@/components/sso/sso-info-card';
import { useToast } from '@/components/ui/hooks/use-toast';
import { validateRouteRole } from '@/lib/routing';
import { BasicInfoCard } from '../-components/basic-info-card';
import { ManagePermissionsDropdown } from '../-components/manage-permissions-dropdown';
import { ReadingSetupCard } from '../-components/reading-setup-card';
import { coachesTabs } from '../-components/tabs';
import { TeachingSectionCard } from '../-components/teaching-section-card';

export const Route = createFileRoute('/_layout/teachers/$teacherId/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: CoachPage,
});

function CoachPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher. Please try again.',
        };
      }}
    >
      <CoachDetail />
    </InfoPageSuspenseBoundary>
  );
}

function CoachDetail() {
  const { toast } = useToast();
  const { teacherId: userId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });
  const { data: coach } = useSuspenseQuery(getCoach(userId));

  const onSubmitSsoSuccess = () => {
    toast({ title: 'Teacher info updated' });
  };
  const onSubmitSsoFailure = (error?: AirServerError) => {
    toast({ title: 'Failed to update teacher SSO', variant: 'destructive', description: error?.userFacingMessage });
  };

  return (
    <div className="mb-2">
      <InfoPage
        headerText={user.fullNameOrEmail}
        headerActions={<ManagePermissionsDropdown user={user} />}
        tabs={coachesTabs({ teacherId: userId })}
        breadcrumbs={{
          linkItems: [
            {
              label: 'Teachers',
              link: {
                to: '/teachers',
              },
            },
          ],
          currentPage: user.fullNameOrEmail ?? '',
        }}
      >
        <>
          <BasicInfoCard coach={coach} userId={userId} />
          <SsoEditableInfoCard user={user} onSuccess={onSubmitSsoSuccess} onError={onSubmitSsoFailure} />
          <ReadingSetupCard coach={coach} userId={userId} />
          <TeachingSectionCard coach={coach} userId={userId} />
        </>
      </InfoPage>
    </div>
  );
}
